import React from 'react';
import { Security, useOktaAuth } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { useNavigate } from 'react-router-dom';
import { ProgressOverlay } from '@signanthealth/modular-ui';

interface SecurityWrapperProps {
  oktaAuth: OktaAuth;
  applicationPath?: string;
  enableGlobalLogout?: boolean;
  children: React.ReactNode;
}

/**
 * Inner component that checks authentication and triggers login redirect
 */
const AuthenticationGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  React.useEffect(() => {
    if (!authState) {
      return; // Still loading
    }

    // Don't redirect if we're on the callback route
    if (window.location.pathname === '/login/callback') {
      return;
    }

    if (!authState.isAuthenticated && !isRedirecting) {
      setIsRedirecting(true);
      // Trigger Okta login redirect
      oktaAuth.signInWithRedirect();
    }
  }, [authState, oktaAuth, isRedirecting]);

  // Allow callback route to render without authentication check
  if (window.location.pathname === '/login/callback') {
    return <>{children}</>;
  }

  // Show loading state while authentication is being determined
  if (!authState) {
    return <ProgressOverlay inProgress={true} fullOverlay message="Loading..." />;
  }

  // Show redirecting message
  if (!authState.isAuthenticated) {
    return <ProgressOverlay inProgress={true} fullOverlay message="Redirecting to login..." />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};

/**
 * SecurityWrapper component for Okta authentication with React Router v6/v7
 * Adapted from @signanthealth/modular-ui SecurityWrapper for compatibility
 */
export const SecurityWrapper: React.FC<SecurityWrapperProps> = ({
  oktaAuth,
  applicationPath = '',
  enableGlobalLogout = true,
  children,
}) => {
  const navigate = useNavigate();

  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    const relativeUri = toRelativeUrl(originalUri || '/', window.location.origin);
    navigate(relativeUri, { replace: true });
  };

  // Handle global logout if enabled
  React.useEffect(() => {
    if (enableGlobalLogout) {
      const handleTabClose = async () => {
        if (oktaAuth.authStateManager.getAuthState()?.isAuthenticated) {
          await oktaAuth.signOut();
        }
      };

      window.addEventListener('beforeunload', handleTabClose);
      return () => window.removeEventListener('beforeunload', handleTabClose);
    }
  }, [oktaAuth, enableGlobalLogout]);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <AuthenticationGate>{children}</AuthenticationGate>
    </Security>
  );
};
